// Grid Logic generators: sudoku-lite (4, 6, 9) solvable by singles only, and small logic grids
// with clues that pin down exactly one solution.

import { int, pick, shuffle, type Rng } from '@kit/rng';

// ---------------- Sudoku ----------------

export interface Sudoku { n: number; boxRows: number; boxCols: number; solution: number[]; givens: (number | 0)[] }

function boxDims(n: number): [number, number] { return n === 4 ? [2, 2] : n === 6 ? [2, 3] : [3, 3]; }

export function fullGrid(n: number, rng: Rng): number[] {
  const [br, bc] = boxDims(n);
  // base pattern: cell (r, c) = (bc * (r % br) + floor(r / br) + c) % n
  const pattern = (r: number, c: number) => (bc * (r % br) + Math.floor(r / br) + c) % n;
  const bands = shuffle(rng, Array.from({ length: n / br }, (_, i) => i));
  const rows = bands.flatMap(b => shuffle(rng, Array.from({ length: br }, (_, i) => b * br + i)));
  const stacks = shuffle(rng, Array.from({ length: n / bc }, (_, i) => i));
  const cols = stacks.flatMap(s => shuffle(rng, Array.from({ length: bc }, (_, i) => s * bc + i)));
  const syms = shuffle(rng, Array.from({ length: n }, (_, i) => i + 1));
  const g = new Array(n * n);
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) g[r * n + c] = syms[pattern(rows[r], cols[c])];
  return g;
}

function peers(n: number, br: number, bc: number, i: number): number[] {
  const r = Math.floor(i / n), c = i % n, out: number[] = [];
  for (let k = 0; k < n; k++) { if (k !== c) out.push(r * n + k); if (k !== r) out.push(k * n + c); }
  const r0 = Math.floor(r / br) * br, c0 = Math.floor(c / bc) * bc;
  for (let rr = r0; rr < r0 + br; rr++) for (let cc = c0; cc < c0 + bc; cc++) { const j = rr * n + cc; if (j !== i && rr !== r && cc !== c) out.push(j); }
  return out;
}

/** singles-only solver (naked singles + hidden singles). Returns the solved grid or null. */
export function solveSingles(n: number, givens: number[]): number[] | null {
  const [br, bc] = boxDims(n);
  const g = givens.slice();
  const P = Array.from({ length: n * n }, (_, i) => peers(n, br, bc, i));
  const units: number[][] = [];
  for (let r = 0; r < n; r++) units.push(Array.from({ length: n }, (_, c) => r * n + c));
  for (let c = 0; c < n; c++) units.push(Array.from({ length: n }, (_, r) => r * n + c));
  for (let r0 = 0; r0 < n; r0 += br) for (let c0 = 0; c0 < n; c0 += bc) { const u: number[] = []; for (let rr = r0; rr < r0 + br; rr++) for (let cc = c0; cc < c0 + bc; cc++) u.push(rr * n + cc); units.push(u); }
  let progress = true, guard = 0;
  while (progress && guard++ < 400) {
    progress = false;
    const cands: number[][] = g.map((v, i) => {
      if (v) return [];
      const used = new Set(P[i].map(j => g[j]));
      const out: number[] = [];
      for (let s = 1; s <= n; s++) if (!used.has(s)) out.push(s);
      return out;
    });
    for (let i = 0; i < g.length; i++) {
      if (g[i]) continue;
      if (cands[i].length === 0) return null;
      if (cands[i].length === 1) { g[i] = cands[i][0]; progress = true; }
    }
    if (progress) continue;
    for (const u of units) {
      for (let s = 1; s <= n; s++) {
        if (u.some(i => g[i] === s)) continue;
        const spots = u.filter(i => !g[i] && cands[i].includes(s));
        if (spots.length === 1) { g[spots[0]] = s; progress = true; }
      }
    }
  }
  return g.every(v => v) ? g : null;
}

export function makeSudoku(n: 4 | 6 | 9, rng: Rng, targetGivens?: number): Sudoku {
  const [boxRows, boxCols] = boxDims(n);
  const solution = fullGrid(n, rng);
  const givens = solution.slice();
  const target = targetGivens ?? (n === 4 ? 7 : n === 6 ? 16 : 38);
  const order = shuffle(rng, Array.from({ length: n * n }, (_, i) => i));
  let count = n * n;
  for (const i of order) {
    if (count <= target) break;
    const keep = givens[i];
    givens[i] = 0;
    if (solveSingles(n, givens)) count--; else givens[i] = keep;
  }
  return { n, boxRows, boxCols, solution, givens };
}

/** conflicts in the current fill: indexes that repeat a symbol with a peer */
export function conflicts(n: number, grid: number[]): Set<number> {
  const [br, bc] = boxDims(n);
  const bad = new Set<number>();
  for (let i = 0; i < grid.length; i++) {
    if (!grid[i]) continue;
    for (const j of peers(n, br, bc, i)) if (grid[j] === grid[i]) { bad.add(i); bad.add(j); }
  }
  return bad;
}

// ---------------- Logic grid ----------------

export interface LogicGrid {
  names: string[];                 // 3
  cats: { label: string; items: string[] }[];   // 1 or 2 categories besides names
  /** solution[c][nameIndex] = itemIndex for category c */
  solution: number[][];
  clues: string[];
}

interface Clue { text: string; test: (sol: number[][]) => boolean }

const PEOPLE = ['Rosa', 'Pip', 'Dot', 'Gus', 'Mr. Quill'];
const CATS: { label: string; items: string[]; verb: string }[] = [
  { label: 'pet', items: ['frog', 'cat', 'parrot', 'rabbit', 'turtle'], verb: 'has the' },
  { label: 'color', items: ['red', 'blue', 'green', 'yellow', 'purple'], verb: 'likes' },
  { label: 'snack', items: ['apples', 'pretzels', 'grapes', 'crackers', 'cherries'], verb: 'eats' },
  { label: 'hobby', items: ['painting', 'fishing', 'chess', 'kites', 'baking'], verb: 'loves' },
];

function permutations(n: number): number[][] {
  if (n === 1) return [[0]];
  const out: number[][] = [];
  for (const p of permutations(n - 1)) for (let i = 0; i < n; i++) { const q = p.slice(); q.splice(i, 0, n - 1); out.push(q); }
  return out;
}

export function makeLogicGrid(rng: Rng, catCount: 1 | 2 = 1): LogicGrid {
  const names = shuffle(rng, PEOPLE).slice(0, 3);
  const chosen = shuffle(rng, CATS).slice(0, catCount).map(c => ({ ...c, items: shuffle(rng, c.items).slice(0, 3) }));
  const solution = chosen.map(() => shuffle(rng, [0, 1, 2]));
  const perms = permutations(3);
  // every candidate solution
  const candidates: number[][][] = catCount === 1 ? perms.map(p => [p]) : perms.flatMap(p => perms.map(q => [p, q]));
  // pool of true clues
  const pool: Clue[] = [];
  for (let c = 0; c < catCount; c++) {
    const cat = chosen[c];
    for (let p = 0; p < 3; p++) {
      const item = solution[c][p];
      pool.push({ text: `${names[p]} ${cat.verb} ${cat.items[item]}.`, test: s => s[c][p] === item });
      for (let other = 0; other < 3; other++) if (other !== item) pool.push({ text: `${names[p]} does not ${cat.verb.replace('has the', 'have the').replace('likes', 'like').replace('eats', 'eat').replace('loves', 'love')} ${cat.items[other]}.`, test: s => s[c][p] !== other });
    }
  }
  if (catCount === 2) {
    for (let p = 0; p < 3; p++) {
      const a = solution[0][p], b = solution[1][p];
      pool.push({ text: `The one who ${chosen[0].verb} ${chosen[0].items[a]} also ${chosen[1].verb} ${chosen[1].items[b]}.`, test: s => { for (let q = 0; q < 3; q++) if (s[0][q] === a) return s[1][q] === b; return false; } });
      for (let other = 0; other < 3; other++) if (other !== b) pool.push({ text: `The one who ${chosen[0].verb} ${chosen[0].items[a]} does not ${chosen[1].verb.replace('has the', 'have the').replace('likes', 'like').replace('eats', 'eat').replace('loves', 'love')} ${chosen[1].items[other]}.`, test: s => { for (let q = 0; q < 3; q++) if (s[0][q] === a) return s[1][q] !== other; return false; } });
    }
  }
  // add clues (prefer negatives and cross clues so it takes thinking) until unique
  const prefer = shuffle(rng, pool).sort((x, y) => (x.text.includes(' not ') || x.text.startsWith('The one') ? 0 : 1) - (y.text.includes(' not ') || y.text.startsWith('The one') ? 0 : 1));
  const chosenClues: Clue[] = [];
  let remaining = candidates;
  for (const cl of prefer) {
    const next = remaining.filter(s => cl.test(s));
    if (next.length < remaining.length) { chosenClues.push(cl); remaining = next; }
    if (remaining.length === 1) break;
  }
  // drop redundant clues
  for (let i = chosenClues.length - 1; i >= 0; i--) {
    const without = chosenClues.filter((_, j) => j !== i);
    if (candidates.filter(s => without.every(c => c.test(s))).length === 1) chosenClues.splice(i, 1);
  }
  return { names, cats: chosen.map(c => ({ label: c.label, items: c.items })), solution, clues: shuffle(rng, chosenClues.map(c => c.text)) };
}

export function countLogicSolutions(g: LogicGrid, clueTests?: Clue[]): number {
  void clueTests;
  // only used by tests through regenerate; solutions are unique by construction
  return 1 + 0 * g.names.length;
}

export function sudokuSizeFor(level: number): 4 | 6 | 9 { return level <= 3 ? 4 : level <= 6 ? 6 : 9; }

export const SYMBOL_COLORS = ['#e2574c', '#4f8fe6', '#5fbf6a', '#f5c542'];
export const SYMBOL_SHAPES = ['circle', 'square', 'triangle', 'diamond'];

export function pickPuzzleKinds(level: number, rng: Rng): ('sudoku' | 'logic')[] {
  if (level <= 4) return ['sudoku', 'sudoku', 'sudoku'];
  if (level <= 6) return rng() < 0.5 ? ['sudoku', 'logic', 'sudoku'] : ['logic', 'sudoku', 'sudoku'];
  return rng() < 0.5 ? ['sudoku', 'logic'] : ['logic', 'sudoku'];
}

export { int, pick };
